import type { InputProps } from '$lib/elements/inputs/types';
import { KYCCategoriesName, KYCQuestionTypeAPI, KYCQuestionType } from '@app/enums/KYC';
import type { SvelteComponent } from 'svelte';
import type { DocumentStatusEnum } from './enums/DocumentStatusEnum';
import { capitalize } from '$lib/utils/strings';
import type { OperationRegulationsEnum } from './enums/OperationRegulationsEnum';
import type { ResultEnum, RevenueEnum } from '@app/models/api/enums/CapitalSegmentsOptions';

export type KYCStepType = {
	name: string;
	title: string;
	slug: string;
	index: number;
	getComponent: () => Promise<typeof SvelteComponent<any>>;
};
export type KYCModelAPI = {
	nom: KYCCategoriesName;
	ordre?: number;
	questions: KYCQuestionAPIModel[];
};
export type KYCIntroduction = {
	id: number;
	title: string;
	subTitle?: string;
	isVisible: boolean;
	itemsList?: string;
	text?: string;
	buttonLabel?: string;
	questionMark?: string;
	buttonAction?: () => Promise<void>;
};

export type KYCModel = {
	name?: KYCCategoriesName;
	order?: number;
	questions?: KYCQuestionProps[];
	introduction?: KYCIntroduction;
};

export type KYCOptionAPI = {
	id: number;
	libelle: string;
};

export type KYCOption = {
	id?: number;
	label?: string;
	value?: string;
	checked?: boolean;
	classNames?: string;
};

export type KYCRequestBody = {
	type: OperationRegulationsEnum;
	questions: KYCRequestQuestionAPI[];
};

export type KYCRequestQuestionAPI = {
	reponses?: KYCOption[];
	reponseLibre?: string;
	id: number;
	type: KYCQuestionTypeAPI;
	libelle: string;
};
export type KYCRequestQuestion = {
	id: number;
	type: KYCQuestionType;
	label: string;
	responses?: KYCOption[];
	responseLibre?: string;
};
export interface KYCQuestionProps extends InputProps {
	renderSpecificValue?: boolean;
	order?: number;
	multiple?: boolean;
	type?: KYCQuestionType;
	showPreview?: boolean;
	showCamera?: boolean;
	title?: string;
	options?: KYCOption[] | { maxDate?: Date };
	introduction?: string;
	specificValue?: SpecificValue | KYCOption | SpecificValue[] | KYCOption[] | null;
	customerResponse?: KYCCustomerResponse | string | any;
	dependsOn?: KYCDepend[];
	valid?: boolean;
	hidden?: boolean;
	placeholder?: string;
	questions?: any[];
	column?: boolean;
	withSearchBar?: boolean;
	accept?: string;
	apiKeys?: string[];
	typeAttestDom?: string;
	typeDocument?: any;
	isVisible?: boolean;
	props?: KYCQuestionProps;
	index?: number;
	state?: DocumentStatusEnum | null;
}

export type SpecificValue = {
	id: number;
	value: string;
};
export type KYCQuestionComponentProps = {
	component: new (args: { target: any; props?: any }) => SvelteComponent<any, any, any>;
	props: KYCQuestionProps;
};

export type KYCQuestion = {
	props: KYCQuestionProps;
};

export type KYCCustomerResponse = {
	response?: string | ResultEnum | RevenueEnum;
	responses?:
		| KYCOption[]
		| KYCOption
		| SpecificValue
		| SpecificValue[]
		| string
		| string[]
		| Record<string, never>;
	validityDate?: Date;
};
export type KYCCustomerResponseAPI = {
	reponse?: string;
	reponses?: KYCOptionAPI[];
	dateValidite?: Date;
};

export type KYCDependAPI = {
	id: number;
	libelle: string;
	idQuestion: number;
};
export type KYCDepend = {
	id: number;
	label: string;
	idQuestion: number;
};

export type KYCQuestionAPIModel = {
	id: number;
	libelle: string;
	description: string;
	ordre: number;
	type: KYCQuestionTypeAPI;
	reponsesPossibles: KYCOptionAPI[];
	reponseClient: KYCCustomerResponseAPI;
	dependsOn?: KYCDependAPI[];
};

export type ProgressBarKYCItem = {
	name: string;
	quantity: number;
};

export type UnansweredQuestions = {
	categorie: {
		name: KYCCategoriesName;
		step: number;
	};
	question?: KYCQuestionProps;
};

export const translateKYCModel = (kycAPI: KYCModelAPI): KYCModel => {
	return {
		name: kycAPI.nom,
		order: kycAPI.ordre,
		questions: [
			...kycAPI.questions.map((question) => {
				const baseQuestion: KYCQuestionProps = {
					id: question.id,
					title: question.libelle,
					description: question.description,
					order: question.ordre,
					type: translateQuestionType(question.type),
					isVisible: true,
					customerResponse:
						question.reponseClient && JSON.stringify(question.reponseClient) !== '{}'
							? {
									...translateKYCResponse(
										translateQuestionType(question.type),
										question.reponseClient
									)
								}
							: {}
				};
				if (question.reponsesPossibles && question.reponsesPossibles.length > 0) {
					baseQuestion.options = translateOptions(question.reponsesPossibles);
				}
				if (question.dependsOn) {
					baseQuestion.dependsOn = translateDepends(question.dependsOn);
					baseQuestion.isVisible = false;
					baseQuestion.classNames = 'hidden';
				}
				return baseQuestion;
			})
		]
	};
};
export const translateKYCResponse = (
	type: KYCQuestionType,
	question: KYCCustomerResponseAPI
): KYCCustomerResponse => {
	switch (type) {
		case KYCQuestionType.MULTIPLE_CHOICE:
			return {
				responses: translateOptions(question.reponses ?? null),
				validityDate: question.dateValidite
			};
		case KYCQuestionType.SINGLE_CHOICE:
			return {
				responses: translateOption(question.reponses ? question.reponses[0] : null),
				validityDate: question.dateValidite
			};
		case KYCQuestionType.INPUT_TEXT:
		case KYCQuestionType.INPUT_NUMBER:
			return { response: question.reponse, validityDate: question.dateValidite };
		default:
			return { responses: {} };
	}
};

export const translateQuestionType = (questionType: KYCQuestionTypeAPI): KYCQuestionType => {
	switch (questionType) {
		case KYCQuestionTypeAPI.CHOIX_MULTIPLE:
			return KYCQuestionType.MULTIPLE_CHOICE;
		case KYCQuestionTypeAPI.CHOIX_UNIQUE:
			return KYCQuestionType.SINGLE_CHOICE;
		case KYCQuestionTypeAPI.SAISIE_TEXT:
			return KYCQuestionType.INPUT_TEXT;
		case KYCQuestionTypeAPI.SAISIE_NOMBRE:
			return KYCQuestionType.INPUT_NUMBER;
	}
};
export const translateQuestionTypeToAPI = (questionType: KYCQuestionType): KYCQuestionTypeAPI => {
	switch (questionType) {
		case KYCQuestionType.MULTIPLE_CHOICE:
			return KYCQuestionTypeAPI.CHOIX_MULTIPLE;
		case KYCQuestionType.SINGLE_CHOICE:
			return KYCQuestionTypeAPI.CHOIX_UNIQUE;
		case KYCQuestionType.INPUT_TEXT:
			return KYCQuestionTypeAPI.SAISIE_TEXT;
		case KYCQuestionType.INPUT_NUMBER:
			return KYCQuestionTypeAPI.SAISIE_NOMBRE;
	}
};

export const translateOptions = (options: KYCOptionAPI[] | null): KYCOption[] => {
	return (options ?? [])
		.filter((element) => element?.libelle)
		.map((element) => {
			return translateOption(element);
		});
};
export const translateDepends = (options: KYCDependAPI[] | []): KYCDepend[] => {
	return (options ?? [])
		.filter((element) => element?.libelle)
		.map((element) => {
			return {
				id: element.id,
				label: element.libelle,
				idQuestion: element.idQuestion
			};
		});
};
export const translateOption = (option: KYCOptionAPI | null): KYCOption | string => {
	return option
		? {
				id: option.id,
				value: option.libelle,
				label: capitalize(option.libelle)
			}
		: '';
};
